import { isBefore } from 'date-fns';
import Decimal from 'decimal.js';
import { compact } from 'lodash-es';
import { BillingType, BillMode, ServiceFormDataType } from '~/components/BillClientModal/BillClientModal.types';
import { manualServiceBillingModel, serviceCostModel, serviceModel } from '~/models';
import { BillingStageType } from '~/models/manual-service-billing';
import { utils } from '~/utils';
import styles from '../BillClientModal.module.scss';
export function getTableDefs(service) {
    const isVariablePrice = serviceCostModel.isVariablePrice(service.cost);
    const isRangePrice = serviceCostModel.isRangePrice(service.cost);
    const discount = serviceModel.discount(service);
    const isPercentDiscount = discount == null || discount.isPercent();
    const tableDefs = compact([
        {
            headerItem: {
                value: 'Service name'
            },
            columnDef: {
                width: 248
            }
        },
        {
            headerItem: {
                value: isVariablePrice ? 'Quantity' : '',
                className: styles.headerAmountColumn
            },
            columnDef: {
                width: 130
            }
        },
        {
            headerItem: {
                value: 'Rate',
                className: styles.headerAmountColumn
            },
            columnDef: {
                width: 130
            }
        },
        {
            headerItem: {
                value: isPercentDiscount ? 'Discount' : 'Unit discount',
                className: styles.headerAmountColumn
            },
            columnDef: {
                width: 130
            }
        },
        {
            headerItem: {
                value: isRangePrice || isVariablePrice ? 'Preapproved' : null,
                className: styles.headerAmountColumn
            },
            columnDef: {
                width: 130
            }
        },
        {
            headerItem: {
                value: 'Total',
                className: styles.headerAmountColumn
            },
            columnDef: {
                width: [
                    130,
                    276
                ]
            }
        }
    ]);
    return {
        columnDefs: tableDefs.map((d)=>d.columnDef),
        headerItems: tableDefs.map((d)=>d.headerItem)
    };
}
export var QtyWarning = /*#__PURE__*/ function(QtyWarning) {
    QtyWarning["Billed"] = "billed";
    QtyWarning["Scheduled"] = "scheduled";
    return QtyWarning;
}({});
export const qtyWarningToText = {
    billed: 'Exceeding the preapproved amount will require your client to manually approve the payment',
    scheduled: "Amount exceeds the preapproved quantity due to a scheduled bill and requires client's approval."
};
export function getRelevantPeriod(manualServiceBilling, issueDate) {
    if (!manualServiceBilling) return undefined;
    return manualServiceBilling.periods.find((p)=>manualServiceBillingModel.isInRange(issueDate, p));
}
export const calculateTotalFormQuantity = (items, serviceId)=>items.reduce((totalQuantity, item)=>{
        if (item.type === BillingType.Service && item.service.id === serviceId) return totalQuantity.plus(new Decimal(item.service.quantity || 0));
        return totalQuantity;
    }, new Decimal(0));
export function getQuantityWarning(params) {
    const { service, quantity, issueDate, manualServiceBilling, relationshipEffectiveDate } = params;
    if (issueDate && relationshipEffectiveDate && isBefore(issueDate, relationshipEffectiveDate)) return null;
    const unitCap = serviceModel.unitCap(service);
    if (serviceCostModel.isVariablePrice(service.cost) && unitCap != null) {
        const relevantPeriod = issueDate && getRelevantPeriod(manualServiceBilling, issueDate);
        const { billedQuantity = new Decimal(0), scheduledQuantity = new Decimal(0) } = relevantPeriod || {};
        if (quantity.plus(billedQuantity).gt(unitCap)) return "billed";
        if (quantity.plus(billedQuantity).plus(scheduledQuantity).gt(unitCap)) return "scheduled";
    }
    return null;
}
export function getTotal(fullService, service, quantity, priceInput) {
    let total = new Decimal(0);
    if (serviceCostModel.isRangePrice(fullService.cost)) {
        total = priceInput ? utils.applyDiscount(+priceInput, serviceModel.discount(fullService)) : new Decimal(0);
    } else if (service.type === ServiceFormDataType.Stages) {
        const specificStage = service.billingStages.find((stage)=>stage.stageNumber === service.stageNumber);
        if (specificStage) {
            total = utils.applyDiscount(specificStage.amount, fullService.cost.discount);
        } else {
            total = utils.applyDiscount(service.billingStages.filter((stage)=>stage.type === BillingStageType.Available)[0].amount, fullService.cost.discount);
        }
    } else {
        total = serviceModel.finalPrice(fullService, quantity);
    }
    return Decimal.max(total, 0);
}
export function getCumulativeWarning(params) {
    const { service, issueDate, manualServiceBilling, relationshipEffectiveDate } = params;
    if (relationshipEffectiveDate && isBefore(issueDate, relationshipEffectiveDate)) return null;
    const relevantPeriod = getRelevantPeriod(manualServiceBilling, issueDate);
    if (!relevantPeriod) {
        return null;
    } else if (serviceCostModel.isVariablePrice(service.cost)) {
        const unitCap = serviceModel.unitCap(service);
        if (!unitCap) return null;
        const { billedQuantity, scheduledQuantity } = relevantPeriod;
        const isWarning = billedQuantity.plus(scheduledQuantity).gt(unitCap);
        return {
            isWarning,
            billedQuantity,
            scheduledQuantity
        };
    } else {
        const { billedQuantity, scheduledQuantity } = relevantPeriod;
        return billedQuantity.gt(0) || scheduledQuantity.gt(0) ? {
            isWarning: true,
            billedQuantity,
            scheduledQuantity
        } : null;
    }
}
export function getBillServiceBeforeItsEffectiveDateWarning(mode, service, issueDate, relationshipEffectiveDate) {
    if (relationshipEffectiveDate != null) return null;
    const serviceEffectiveDate = serviceModel.effectiveDate(service);
    if (mode === BillMode.Now && serviceEffectiveDate && isBefore(issueDate, serviceEffectiveDate)) return {
        text: 'As the issue date is earlier than the date this service was added to the agreement, your client will need to manually approve the payment.'
    };
    return null;
}
